// Entry point — boot sequence, global function registry for inline onclick="" handlers
// (kept from the original app's architecture: a big static HTML shell with ids, JS
// renders into it), and wiring between all the ported modules.
//
// Boot sequence (see lib/firebase.js for why this order matters): show a lightweight
// loading state, await waitForAuthReady() — which resolves only once we know for CERTAIN
// whether a user is signed in — and only then decide between the login gate and the app.
// The previous version showed the login gate by default and hid it once Firebase
// resolved; that flash looked like "logs out on every refresh". Never reintroduce it.
import './style/index.css';

import { waitForAuthReady, subscribeAuth, login } from './lib/firebase.js';
import {
  setCurrentUser, setOnRemoteChange, attachFirestoreSync, detachFirestoreSync,
  initNetworkWatch, checkSyncNow,
} from './lib/sync.js';
import { loadFromCache } from './state.js';
import { buildNav, goNav, openMoreMenu, renderAll } from './lib/router.js';
import { today } from './lib/dates.js';
import { wireBackdrops, openModal, closeModal, openSheet, closeSheet } from './lib/modal.js';
import { confirmPasswordDialog } from './components/passwordDialog.js';
import { confirmValuePrompt } from './components/valuePrompt.js';
import { dpPrevMonth, dpNextMonth, dpPick } from './components/datePicker.js';
import {
  openTemplates, applyTemplate, delTemplate, setStType, setStMeal, openSaveTemplate, saveTemplate,
} from './components/templates.js';
import {
  doExport, handleRestoreFile, confirmRestore, toggleExpType, setExpFormat, openExportModal, runExport,
  onExpRangeChange, setExpQuickRange, openBackupRestore, findDailyBackup, confirmBackupRestore,
} from './lib/exportData.js';

import {
  renderHome, toggleWeightVisibility, toggleWeightPart, openLogWeight, saveLogWeight, editLastWeight, deleteLastWeight,
  calcWeightRange,
} from './pages/home.js';
import {
  renderFood, shiftFoodDate, setFoodDate, openFoodDatePicker, goToFoodDate, toggleFoodEaten, delFoodItem,
  openFoodEdit, cancelAddFood, openAddFood, setActiveMeal, toggleFEaten, fToggleAddRow, fStartReplaceIng,
  fCancelReplaceIng, foodNameSearch, selectFoodItem, fIngSearch, fIngSelect, fIngAdd, fDelIng,
  fSetIngGrams, fTogglePortionAll, fToggleNoShrink, fRecalcPortion, addFoodEntry, fEditNestedDish,
} from './pages/food.js';
import {
  renderWorkout, shiftWkDate, setWkDate, openWkDatePicker, toggleWkRest, openAddWorkout, editWorkout,
  toggleWkDetails, toggleWkCardio, setWkStr, saveWorkout, delWorkout,
  openWeeklyPlan, toggleWeeklyPlanRest, toggleWeeklyPlanCardio, setWeeklyPlanStr,
} from './pages/workout.js';
import {
  renderVitamins, shiftVitDate, setVitDate, toggleVit, openVitConfig, saveVitSlotField, addVitSlot, delVitSlot,
  openVitItemNew, openVitItemEdit, delVitItem, saveVitItem, showVitSlotAnyway, saveVitShowOnRest,
} from './pages/vitamins.js';
import {
  renderDishesPage, switchDishTab, renderDishes, toggleDishFavorite, dIngSearch, dIngSelect, dIngAdd,
  dDelIng, dSetIngGrams, dCalcTotals, dToggleNoShrink, dEditNestedDish, dCloseModal, openAddDish, editDish, saveDish, delDish,
  renderIngs, openAddIng, openEditIng, saveIng, delIng, toggleIngAvail,
} from './pages/dishes.js';
import {
  renderMeasurements, switchMeasTab, setMeasType, openAddMeasurement, saveMeasurement, delMeasurement,
} from './pages/measurements.js';
import {
  renderArchive, switchArchiveTab, selectArchMonth, deleteArchWorkout, editArchWeight, deleteArchWeight,
  toggleArchWeightVisibility,
} from './pages/archive.js';
import {
  renderSettings, setAuthUserForSettings, saveSet, applyTheme, setTheme, uploadCustomIcon,
  resetCustomIcon, applyCustomIcon, tgTest, confirmResetAll, doLogoutFromSettings,
} from './pages/settings.js';

// ── Global registry for inline onclick="" handlers in index.html and in template
// strings rendered by the page modules. This mirrors the original single-file app's
// architecture (global functions bound to onclick) rather than rewriting every handler
// to addEventListener, which would balloon the size of this port without changing
// behavior — the risk of missing one of ~100 handlers during a rewrite was judged higher
// than the (small, well-understood) cost of a global registry in a single-user PWA. ──
Object.assign(window, {
  // modal/sheet primitives
  openModal, closeModal, openSheet, closeSheet,
  // nav
  goNav, openMoreMenu,
  // auth
  doLogin, doLogoutFromSettings,
  // sync
  checkSyncNow,
  // hard refresh (mobile-only button in Settings) — cache-busts the URL, does NOT touch auth
  hardRefresh,
  // templates
  openTemplates, applyTemplate, delTemplate, setStType, setStMeal, openSaveTemplate, saveTemplate,
  // export/restore
  doExport, handleRestoreFile, confirmRestore, toggleExpType, setExpFormat, openExportModal, runExport,
  onExpRangeChange, setExpQuickRange, openBackupRestore, findDailyBackup, confirmBackupRestore,
  // password / confirm / value-prompt dialogs
  confirmPasswordDialog, confirmValuePrompt,
  // date picker
  dpPrevMonth, dpNextMonth, dpPick,
  // home / weight
  toggleWeightVisibility, toggleWeightPart, openLogWeight, saveLogWeight, editLastWeight, deleteLastWeight,
  calcWeightRange,
  // food
  shiftFoodDate, setFoodDate, openFoodDatePicker, goToFoodDate, toggleFoodEaten, delFoodItem, openFoodEdit,
  cancelAddFood, openAddFood, setActiveMeal, toggleFEaten, fToggleAddRow, fStartReplaceIng,
  fCancelReplaceIng, foodNameSearch, selectFoodItem, fIngSearch, fIngSelect, fIngAdd, fDelIng,
  fSetIngGrams, fTogglePortionAll, fToggleNoShrink, fRecalcPortion, addFoodEntry, fEditNestedDish,
  // workout
  shiftWkDate, setWkDate, openWkDatePicker, toggleWkRest, openAddWorkout, editWorkout, toggleWkDetails,
  toggleWkCardio, setWkStr, saveWorkout, delWorkout,
  openWeeklyPlan, toggleWeeklyPlanRest, toggleWeeklyPlanCardio, setWeeklyPlanStr,
  // vitamins
  shiftVitDate, setVitDate, toggleVit, openVitConfig, saveVitSlotField, addVitSlot, delVitSlot, openVitItemNew,
  openVitItemEdit, delVitItem, saveVitItem, showVitSlotAnyway, saveVitShowOnRest,
  // dishes / ingredients
  switchDishTab, renderDishes, toggleDishFavorite, dIngSearch, dIngSelect, dIngAdd, dDelIng,
  dSetIngGrams, dCalcTotals, dToggleNoShrink, dEditNestedDish, dCloseModal, openAddDish, editDish, saveDish, delDish, renderIngs, openAddIng,
  openEditIng, saveIng, delIng, toggleIngAvail,
  // measurements
  switchMeasTab, setMeasType, openAddMeasurement, saveMeasurement, delMeasurement,
  // archive
  switchArchiveTab, selectArchMonth, deleteArchWorkout, editArchWeight, deleteArchWeight,
  toggleArchWeightVisibility,
  // settings
  saveSet, setTheme, uploadCustomIcon, resetCustomIcon, tgTest, confirmResetAll,
});

// ── Register every page's render function so goNav()/renderAll() can find them ──
// (import side-effects already called registerPage() in each page module — nothing to
// do here beyond having imported them, which the import list above already does.)

// If the app is left open (foreground or backgrounded PWA) across midnight, nothing was
// re-rendering it — Home kept showing yesterday's date/greeting/КБЖУ until something else
// (like a nav tap) happened to trigger a render. Poll for the calendar date actually
// changing and re-render whatever page is open when it does.
function watchForDateRollover() {
  let lastKnownDate = today();
  setInterval(() => {
    const d = today();
    if (d !== lastKnownDate) {
      lastKnownDate = d;
      renderAll();
    }
  }, 60000);
}

function hardRefresh() {
  // A plain location.reload() can still serve a stale cached copy, especially in iOS
  // "Add to Home Screen" mode — this forces a genuinely fresh fetch by cache-busting the
  // URL. Does NOT touch auth — Firebase session persists across this like a normal reload.
  const url = new URL(location.href);
  url.searchParams.set('_r', Date.now());
  location.href = url.toString();
}

function doLogin() {
  const email = document.getElementById('authEmail').value.trim();
  const pass = document.getElementById('authPass').value;
  const errEl = document.getElementById('authError');
  const btn = document.getElementById('authLoginBtn');
  errEl.style.display = 'none';
  if (!email || !pass) { errEl.textContent = 'Введи email и пароль'; errEl.style.display = 'block'; return; }
  btn.disabled = true; btn.textContent = 'Вход…';
  login(email, pass)
    .catch(err => {
      const known = {
        'auth/invalid-credential': 'неверный email или пароль', 'auth/wrong-password': 'неверный пароль',
        'auth/user-not-found': 'такого пользователя нет', 'auth/invalid-email': 'некорректный email',
        'auth/too-many-requests': 'слишком много попыток, попробуй позже',
      };
      errEl.textContent = 'Не удалось войти: ' + (known[err.code] || err.message);
      errEl.style.display = 'block';
    })
    .finally(() => { btn.disabled = false; btn.textContent = 'Войти'; });
}

function showAppShell() {
  document.getElementById('authGate').style.display = 'none';
  buildNav();
  applyTheme();
  applyCustomIcon();
  goNav('home');
}

function showAuthGate() {
  document.getElementById('authGate').style.display = 'flex';
}

let appBooted = false;

async function boot() {
  wireBackdrops();
  loadFromCache(); // instant local snapshot so the app has something to render immediately

  const user = await waitForAuthReady();
  document.getElementById('bootLoading').style.display = 'none';

  if (!user) {
    showAuthGate();
  } else {
    setCurrentUser(user);
    setAuthUserForSettings(user);
    showAppShell();
    appBooted = true;
    setOnRemoteChange(() => {
      // Data loaded asynchronously after first paint — re-render whatever is open now
      // rather than leaving the DOM stale (known-bugs #8).
      renderAll();
    });
    attachFirestoreSync();
    initNetworkWatch();
    watchForDateRollover();
  }

  // React to sign-in/out that happens *after* boot (e.g. logging in from the gate, or
  // logging out from Settings). waitForAuthReady() already handled the initial decision;
  // this only handles transitions from here on.
  subscribeAuth(user => {
    if (user) {
      if (!appBooted) {
        setCurrentUser(user);
        setAuthUserForSettings(user);
        showAppShell();
        appBooted = true;
        setOnRemoteChange(() => renderAll());
        attachFirestoreSync();
        initNetworkWatch();
      }
    } else if (appBooted) {
      detachFirestoreSync();
      setCurrentUser(null);
      appBooted = false;
      showAuthGate();
    }
  });
}

boot();
